import { useCallback, useEffect, useState } from "react";

// Tipo do evento que Chrome/Edge/Android disparam quando o site é instalável.
// (Não está nos lib.dom.d.ts oficiais ainda em todas as versões.)
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const ADIAR_KEY = "pwa_install_adiado_ate";
// 7 dias em ms — depois desse prazo o banner volta a aparecer
const ADIAR_DURACAO_MS = 7 * 24 * 60 * 60 * 1000;

function ehStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari expõe navigator.standalone fora do padrão
  if ((window.navigator as any).standalone === true) return true;
  return false;
}

function ehIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad recente se identifica como Mac — desambigua via touch
  const tocaTela = (navigator as any).maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && tocaTela);
}

function adiamentoAtivo(): boolean {
  try {
    const ate = Number(localStorage.getItem(ADIAR_KEY) || 0);
    return ate > Date.now();
  } catch {
    return false;
  }
}

export function usePwaInstall() {
  const [evento, setEvento] = useState<BeforeInstallPromptEvent | null>(null);
  const [standalone, setStandalone] = useState<boolean>(ehStandalone());
  const [adiado, setAdiado] = useState<boolean>(adiamentoAtivo());
  const ios = ehIos();

  useEffect(() => {
    function onBefore(e: Event) {
      // Impede o mini-banner nativo do Chrome desktop — preferimos nosso UI
      e.preventDefault();
      setEvento(e as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setEvento(null);
      setStandalone(true);
    }
    function onDisplayMode() {
      setStandalone(ehStandalone());
    }
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia?.("(display-mode: standalone)");
    mq?.addEventListener?.("change", onDisplayMode);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onDisplayMode);
    };
  }, []);

  const adiar = useCallback(() => {
    try { localStorage.setItem(ADIAR_KEY, String(Date.now() + ADIAR_DURACAO_MS)); } catch {}
    setAdiado(true);
  }, []);

  const limparAdiamento = useCallback(() => {
    try { localStorage.removeItem(ADIAR_KEY); } catch {}
    setAdiado(false);
  }, []);

  const instalar = useCallback(async (): Promise<"accepted" | "dismissed" | "indisponivel"> => {
    if (!evento) return "indisponivel";
    try {
      await evento.prompt();
      const r = await evento.userChoice;
      // Após uso (aceito ou recusado) o evento não pode ser reusado
      setEvento(null);
      if (r.outcome === "dismissed") adiar();
      return r.outcome;
    } catch {
      return "indisponivel";
    }
  }, [evento, adiar]);

  // Pode oferecer instalação?
  // - Já está em standalone → não
  // - Usuário pediu pra adiar → não (até expirar)
  // - Tem evento (Chrome/Edge/Android) OU é iOS (com instruções manuais) → sim
  const podeInstalar = !standalone && !adiado && (!!evento || ios);

  return {
    podeInstalar,
    standalone,
    ios,
    temEventoNativo: !!evento,
    instalar,
    adiar,
    limparAdiamento,
  };
}
